// /opt/isp_erp/frontend/src/pages/admin/finanzas/ReportesFinancieros.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
  DateRange as DateRangeIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { finanzasAPI } from '../../../api/finanzas/finanzas.api';
import api from '../../../api/axios';

const ReportesFinancieros = () => {
  const [loading, setLoading] = useState(false);
  const [reporteDiario, setReporteDiario] = useState(null);
  const [reporteMensual, setReporteMensual] = useState(null);
  const [reporteRango, setReporteRango] = useState(null);
  const [resumenSucursales, setResumenSucursales] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [puntosCobro, setPuntosCobro] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);

  const [filtros, setFiltros] = useState({
    fecha: new Date().toISOString().split('T')[0],
    año: new Date().getFullYear(),
    mes: new Date().getMonth() + 1,
    fecha_desde: '',
    fecha_hasta: '',
    sucursal_id: '',
    punto_cobro_id: '',
    cobrador_id: '',
    metodo_pago: '',
    cuenta_bancaria_id: ''
  });

  const metodosPago = [
    { value: '', label: 'Todos' },
    { value: 'EFECTIVO', label: '💵 Efectivo' },
    { value: 'TRANSFERENCIA', label: '🏦 Transferencia' },
    { value: 'TARJETA_CREDITO', label: '💳 Tarjeta Crédito' },
    { value: 'TARJETA_DEBITO', label: '💳 Tarjeta Débito' },
    { value: 'DEPOSITO', label: '🏧 Depósito' },
    { value: 'CHEQUE', label: '📝 Cheque' }
  ];

  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  const cargarDatosIniciales = async () => {
    await Promise.all([
      cargarSucursales(),
      cargarResumenSucursales(),
      cargarUsuarios(),
      cargarPuntosCobro(),
      cargarCuentasBancarias(),
      cargarReporteDiario()
    ]);
  };

  const cargarSucursales = async () => {
    try {
      const response = await finanzasAPI.getSucursales();
      setSucursales(response.data);
    } catch (err) {
      console.error('Error cargando sucursales:', err);
    }
  };

  const cargarUsuarios = async () => {
    try {
      const response = await api.get('/users');
      setUsuarios(response.data || []);
    } catch (err) {
      console.error('Error cargando usuarios:', err);
    }
  };

  const cargarPuntosCobro = async () => {
    try {
      const response = await finanzasAPI.getPuntosCobro();
      setPuntosCobro(response.data || []);
    } catch (err) {
      console.error('Error cargando puntos de cobro:', err);
    }
  };

  const cargarCuentasBancarias = async () => {
    try {
      const response = await finanzasAPI.getCuentasBancarias();
      setCuentasBancarias(response.data || []);
    } catch (err) {
      console.error('Error cargando cuentas bancarias:', err);
    }
  };

  const cargarResumenSucursales = async () => {
    try {
      const response = await finanzasAPI.getResumenSucursales();
      setResumenSucursales(response.data);
    } catch (err) {
      console.error('Error cargando resumen:', err);
    }
  };

  const cargarReporteDiario = async () => {
    setLoading(true);
    try {
      const params = {
        fecha: filtros.fecha,
        sucursal_id: filtros.sucursal_id || undefined,
        punto_cobro_id: filtros.punto_cobro_id || undefined,
        cobrador_id: filtros.cobrador_id || undefined,
        metodo_pago: filtros.metodo_pago || undefined,
        cuenta_bancaria_id: filtros.cuenta_bancaria_id || undefined
      };
      const response = await finanzasAPI.getReporteCobrosDiario(params);
      setReporteDiario(response.data);
    } catch (err) {
      console.error('Error cargando reporte diario:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarReporteMensual = async () => {
    setLoading(true);
    try {
      const params = {
        año: filtros.año,
        mes: filtros.mes,
        sucursal_id: filtros.sucursal_id || undefined,
        punto_cobro_id: filtros.punto_cobro_id || undefined,
        cobrador_id: filtros.cobrador_id || undefined,
        metodo_pago: filtros.metodo_pago || undefined,
        cuenta_bancaria_id: filtros.cuenta_bancaria_id || undefined
      };
      const response = await finanzasAPI.getReporteCobrosMensual(params);
      setReporteMensual(response.data);
    } catch (err) {
      console.error('Error cargando reporte mensual:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarReporteRango = async () => {
    if (!filtros.fecha_desde || !filtros.fecha_hasta) {
      alert('Seleccione ambas fechas');
      return;
    }
    setLoading(true);
    try {
      const params = {
        fecha_desde: filtros.fecha_desde,
        fecha_hasta: filtros.fecha_hasta,
        sucursal_id: filtros.sucursal_id || undefined,
        punto_cobro_id: filtros.punto_cobro_id || undefined,
        cobrador_id: filtros.cobrador_id || undefined,
        metodo_pago: filtros.metodo_pago || undefined,
        cuenta_bancaria_id: filtros.cuenta_bancaria_id || undefined
      };
      const response = await finanzasAPI.getReporteCobrosPorRango(params);
      setReporteRango(response.data);
    } catch (err) {
      console.error('Error cargando reporte por rango:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const handleExportarPDF = async () => {
    try {
      const params = {
        fecha: filtros.fecha,
        sucursal_id: filtros.sucursal_id || undefined,
        punto_cobro_id: filtros.punto_cobro_id || undefined,
        cobrador_id: filtros.cobrador_id || undefined,
        metodo_pago: filtros.metodo_pago || undefined,
        cuenta_bancaria_id: filtros.cuenta_bancaria_id || undefined
      };
      const response = await finanzasAPI.exportarReportePDF(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando PDF:', err);
      alert('Error al exportar PDF');
    }
  };

  const handleExportarMensualPDF = async () => {
    try {
      const params = {
        año: filtros.año,
        mes: filtros.mes,
        sucursal_id: filtros.sucursal_id || undefined,
        punto_cobro_id: filtros.punto_cobro_id || undefined,
        cobrador_id: filtros.cobrador_id || undefined,
        metodo_pago: filtros.metodo_pago || undefined,
        cuenta_bancaria_id: filtros.cuenta_bancaria_id || undefined
      };
      const response = await finanzasAPI.exportarReporteMensualPDF(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando PDF mensual:', err);
      alert('Error al exportar PDF mensual');
    }
  };

  const handleExportarRangoPDF = async () => {
    if (!reporteRango) return;
    try {
      const params = {
        fecha_desde: filtros.fecha_desde,
        fecha_hasta: filtros.fecha_hasta,
        sucursal_id: filtros.sucursal_id || undefined,
        punto_cobro_id: filtros.punto_cobro_id || undefined,
        cobrador_id: filtros.cobrador_id || undefined,
        metodo_pago: filtros.metodo_pago || undefined,
        cuenta_bancaria_id: filtros.cuenta_bancaria_id || undefined
      };
      const response = await finanzasAPI.exportarReporteRangoPDF(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando PDF del rango:', err);
      alert('Error al exportar PDF');
    }
  };

  const formatMoneda = (valor) => {
    return new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(valor || 0);
  };

  const limpiarFiltrosDiario = () => {
    setFiltros(prev => ({
      ...prev,
      sucursal_id: '',
      punto_cobro_id: '',
      cobrador_id: '',
      metodo_pago: '',
      cuenta_bancaria_id: ''
    }));
    setTimeout(() => cargarReporteDiario(), 100);
  };

  const limpiarFiltrosMensual = () => {
    setFiltros(prev => ({
      ...prev,
      sucursal_id: '',
      punto_cobro_id: '',
      cobrador_id: '',
      metodo_pago: '',
      cuenta_bancaria_id: ''
    }));
    setTimeout(() => cargarReporteMensual(), 100);
  };

  const limpiarFiltrosRango = () => {
    setFiltros(prev => ({
      ...prev,
      fecha_desde: '',
      fecha_hasta: '',
      sucursal_id: '',
      punto_cobro_id: '',
      cobrador_id: '',
      metodo_pago: '',
      cuenta_bancaria_id: ''
    }));
    setReporteRango(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        📊 Reportes Financieros
      </Typography>

      {/* Tarjetas de resumen por sucursal */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Resumen por Sucursal
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenSucursales.map((suc) => (
          <Grid item xs={12} sm={6} md={3} key={suc.id}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {suc.nombre}
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatMoneda(suc.total_cobrado)}
                </Typography>
                <Typography variant="caption">
                  {suc.cantidad_transacciones} transacciones
                </Typography>
                {suc.por_metodo && (
                  <Box sx={{ mt: 1 }}>
                    {Object.entries(suc.por_metodo).slice(0, 2).map(([metodo, monto]) => (
                      <Typography key={metodo} variant="caption" display="block">
                        {metodo}: {formatMoneda(monto)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Reporte Diario */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          <TodayIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Cobros del Día
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              type="date"
              label="Fecha"
              name="fecha"
              value={filtros.fecha}
              onChange={handleFiltroChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sucursal</InputLabel>
              <Select
                name="sucursal_id"
                value={filtros.sucursal_id}
                onChange={handleFiltroChange}
                label="Sucursal"
              >
                <MenuItem value="">Todas</MenuItem>
                {sucursales.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Cobrador</InputLabel>
              <Select
                name="cobrador_id"
                value={filtros.cobrador_id}
                onChange={handleFiltroChange}
                label="Cobrador"
              >
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.username || u.nombre || u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Método de Pago</InputLabel>
              <Select
                name="metodo_pago"
                value={filtros.metodo_pago}
                onChange={handleFiltroChange}
                label="Método de Pago"
              >
                {metodosPago.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Punto de Cobro</InputLabel>
              <Select
                name="punto_cobro_id"
                value={filtros.punto_cobro_id}
                onChange={handleFiltroChange}
                label="Punto de Cobro"
              >
                <MenuItem value="">Todos</MenuItem>
                {puntosCobro.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Cuenta Bancaria</InputLabel>
              <Select
                name="cuenta_bancaria_id"
                value={filtros.cuenta_bancaria_id}
                onChange={handleFiltroChange}
                label="Cuenta Bancaria"
              >
                <MenuItem value="">Todas</MenuItem>
                {cuentasBancarias.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.banco} - {c.numero_cuenta}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                onClick={cargarReporteDiario}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'Consultar'}
              </Button>
              <Button
                variant="outlined"
                onClick={limpiarFiltrosDiario}
                disabled={loading}
              >
                Limpiar
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportarPDF}
                disabled={!reporteDiario}
              >
                PDF
              </Button>
            </Box>
          </Grid>
        </Grid>

        {reporteDiario && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Total cobrado: <strong>{formatMoneda(reporteDiario.total_cobrado)}</strong>
              {' '} - {reporteDiario.cantidad_transacciones} transacciones
            </Alert>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" color="textSecondary">Por Método</Typography>
                  {Object.entries(reporteDiario.por_metodo || {}).map(([metodo, monto]) => (
                    <Typography key={metodo} variant="body2">
                      {metodo}: {formatMoneda(monto)}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" color="textSecondary">Por Cobrador</Typography>
                  {Object.entries(reporteDiario.por_cobrador || {}).map(([cobrador, monto]) => (
                    <Typography key={cobrador} variant="body2">
                      {cobrador}: {formatMoneda(monto)}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" color="textSecondary">Por Punto de Cobro</Typography>
                  {Object.entries(reporteDiario.por_punto_cobro || {}).slice(0, 3).map(([punto, monto]) => (
                    <Typography key={punto} variant="body2">
                      {punto}: {formatMoneda(monto)}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            </Grid>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Identificación</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>Cobrador</TableCell>
                    <TableCell>Punto Cobro</TableCell>
                    <TableCell>Cuenta Bancaria</TableCell>
                    <TableCell>Factura</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reporteDiario.detalle?.map((pago, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{pago.cliente}</TableCell>
                      <TableCell>{pago.cliente_identificacion || '-'}</TableCell>
                      <TableCell align="right">{formatMoneda(pago.monto)}</TableCell>
                      <TableCell>
                        <Chip label={pago.metodo} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{pago.cobrador}</TableCell>
                      <TableCell>{pago.punto_cobro}</TableCell>
                      <TableCell>{pago.cuenta_bancaria || '-'}</TableCell>
                      <TableCell>{pago.factura || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Reporte Mensual */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Cobros del Mes
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={2}>
            <TextField
              type="number"
              label="Año"
              name="año"
              value={filtros.año}
              onChange={handleFiltroChange}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select
                name="mes"
                value={filtros.mes}
                onChange={handleFiltroChange}
                label="Mes"
              >
                <MenuItem value={1}>Enero</MenuItem>
                <MenuItem value={2}>Febrero</MenuItem>
                <MenuItem value={3}>Marzo</MenuItem>
                <MenuItem value={4}>Abril</MenuItem>
                <MenuItem value={5}>Mayo</MenuItem>
                <MenuItem value={6}>Junio</MenuItem>
                <MenuItem value={7}>Julio</MenuItem>
                <MenuItem value={8}>Agosto</MenuItem>
                <MenuItem value={9}>Septiembre</MenuItem>
                <MenuItem value={10}>Octubre</MenuItem>
                <MenuItem value={11}>Noviembre</MenuItem>
                <MenuItem value={12}>Diciembre</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sucursal</InputLabel>
              <Select
                name="sucursal_id"
                value={filtros.sucursal_id}
                onChange={handleFiltroChange}
                label="Sucursal"
              >
                <MenuItem value="">Todas</MenuItem>
                {sucursales.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Cobrador</InputLabel>
              <Select
                name="cobrador_id"
                value={filtros.cobrador_id}
                onChange={handleFiltroChange}
                label="Cobrador"
              >
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.username || u.nombre || u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Método de Pago</InputLabel>
              <Select
                name="metodo_pago"
                value={filtros.metodo_pago}
                onChange={handleFiltroChange}
                label="Método de Pago"
              >
                {metodosPago.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Cuenta Bancaria</InputLabel>
              <Select
                name="cuenta_bancaria_id"
                value={filtros.cuenta_bancaria_id}
                onChange={handleFiltroChange}
                label="Cuenta Bancaria"
              >
                <MenuItem value="">Todas</MenuItem>
                {cuentasBancarias.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.banco} - {c.numero_cuenta}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={cargarReporteMensual}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Consultar'}
              </Button>
              <Button
                variant="outlined"
                onClick={limpiarFiltrosMensual}
                disabled={loading}
              >
                Limpiar filtros
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportarMensualPDF}
                disabled={!reporteMensual}
              >
                PDF Mensual
              </Button>
            </Box>
          </Grid>
        </Grid>

        {reporteMensual && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Total cobrado en el mes: <strong>{formatMoneda(reporteMensual.total_cobrado)}</strong>
              {' '} - Promedio diario: {formatMoneda(reporteMensual.promedio_diario)}
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Por día</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Día</TableCell>
                        <TableCell align="right">Monto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(reporteMensual.por_dia || {}).map(([dia, monto]) => (
                        <TableRow key={dia}>
                          <TableCell>{dia}</TableCell>
                          <TableCell align="right">{formatMoneda(monto)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Por método de pago</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Método</TableCell>
                        <TableCell align="right">Monto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(reporteMensual.por_metodo || {}).map(([metodo, monto]) => (
                        <TableRow key={metodo}>
                          <TableCell>{metodo}</TableCell>
                          <TableCell align="right">{formatMoneda(monto)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Por cobrador</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cobrador</TableCell>
                        <TableCell align="right">Monto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(reporteMensual.por_cobrador || {}).map(([cobrador, monto]) => (
                        <TableRow key={cobrador}>
                          <TableCell>{cobrador}</TableCell>
                          <TableCell align="right">{formatMoneda(monto)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Reporte por Rango de Fechas */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          <DateRangeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Cobros por Rango de Fechas
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              type="date"
              label="Fecha Desde"
              name="fecha_desde"
              value={filtros.fecha_desde}
              onChange={handleFiltroChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              type="date"
              label="Fecha Hasta"
              name="fecha_hasta"
              value={filtros.fecha_hasta}
              onChange={handleFiltroChange}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sucursal</InputLabel>
              <Select
                name="sucursal_id"
                value={filtros.sucursal_id}
                onChange={handleFiltroChange}
                label="Sucursal"
              >
                <MenuItem value="">Todas</MenuItem>
                {sucursales.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Cobrador</InputLabel>
              <Select
                name="cobrador_id"
                value={filtros.cobrador_id}
                onChange={handleFiltroChange}
                label="Cobrador"
              >
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.username || u.nombre || u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Método de Pago</InputLabel>
              <Select
                name="metodo_pago"
                value={filtros.metodo_pago}
                onChange={handleFiltroChange}
                label="Método de Pago"
              >
                {metodosPago.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Punto de Cobro</InputLabel>
              <Select
                name="punto_cobro_id"
                value={filtros.punto_cobro_id}
                onChange={handleFiltroChange}
                label="Punto de Cobro"
              >
                <MenuItem value="">Todos</MenuItem>
                {puntosCobro.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Cuenta Bancaria</InputLabel>
              <Select
                name="cuenta_bancaria_id"
                value={filtros.cuenta_bancaria_id}
                onChange={handleFiltroChange}
                label="Cuenta Bancaria"
              >
                <MenuItem value="">Todas</MenuItem>
                {cuentasBancarias.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.banco} - {c.numero_cuenta}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={cargarReporteRango}
              disabled={loading || !filtros.fecha_desde || !filtros.fecha_hasta}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Consultar'}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={limpiarFiltrosRango}
                disabled={loading}
              >
                Limpiar filtros
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportarRangoPDF}
                disabled={!reporteRango}
              >
                PDF Rango
              </Button>
            </Box>
          </Grid>
        </Grid>

        {reporteRango && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Periodo: {reporteRango.fecha_desde} al {reporteRango.fecha_hasta}
              <br />
              Total cobrado: <strong>{formatMoneda(reporteRango.total_cobrado)}</strong>
              {' '} - {reporteRango.cantidad_transacciones} transacciones
              {' '} - Promedio diario: {formatMoneda(reporteRango.promedio_diario)}
            </Alert>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" color="textSecondary">Por Método</Typography>
                  {Object.entries(reporteRango.por_metodo || {}).map(([metodo, monto]) => (
                    <Typography key={metodo} variant="body2">
                      {metodo}: {formatMoneda(monto)}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" color="textSecondary">Por Cobrador</Typography>
                  {Object.entries(reporteRango.por_cobrador || {}).map(([cobrador, monto]) => (
                    <Typography key={cobrador} variant="body2">
                      {cobrador}: {formatMoneda(monto)}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="caption" color="textSecondary">Por Día</Typography>
                  {Object.entries(reporteRango.por_dia || {}).map(([dia, monto]) => (
                    <Typography key={dia} variant="body2">
                      {dia}: {formatMoneda(monto)}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            </Grid>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Identificación</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>Cobrador</TableCell>
                    <TableCell>Punto Cobro</TableCell>
                    <TableCell>Cuenta Bancaria</TableCell>
                    <TableCell>Factura</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reporteRango.detalle?.map((pago, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{new Date(pago.fecha).toLocaleDateString()}</TableCell>
                      <TableCell>{pago.cliente}</TableCell>
                      <TableCell>{pago.cliente_identificacion || '-'}</TableCell>
                      <TableCell align="right">{formatMoneda(pago.monto)}</TableCell>
                      <TableCell>
                        <Chip label={pago.metodo} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{pago.cobrador}</TableCell>
                      <TableCell>{pago.punto_cobro}</TableCell>
                      <TableCell>{pago.cuenta_bancaria || '-'}</TableCell>
                      <TableCell>{pago.factura || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ReportesFinancieros;
